// business.js — Phase 3.10: Business Intelligence
// Revenue · Capacity · Pipeline · Retention

import { apiFetch, formatCurrency, runManualAction } from './utils.js';
import { state } from './state.js';

export function initBusiness() {
  window.renderBusinessView      = renderBusinessView;
  window.openProspectModal       = openProspectModal;
  window.switchProspectTab       = switchProspectTab;
  window.updateProspectStatus    = updateProspectStatus;
  window.saveProspectNotes       = saveProspectNotes;
  window.refreshProspectDossier  = refreshProspectDossier;
  window.convertProspectToClient = convertProspectToClient;
  window.openOrdealTranscriptModal = openOrdealTranscriptModal;
}

export async function renderBusinessView() {
  state.currentView = 'business';
  const container = document.getElementById('view-business');
  if (!container) return;

  container.innerHTML = '<div class="loading-state">Loading business data…</div>';

  try {
    const year = new Date().getFullYear();
    const [revenue, capacity, pipeline, retention, prospects, grimoireSummary, grimoireSessions] = await Promise.all([
      apiFetch(`/api/business/revenue?year=${year}`).catch(() => ({})),
      apiFetch('/api/business/capacity').catch(() => ({})),
      apiFetch('/api/business/pipeline').catch(() => []),
      apiFetch('/api/business/retention').catch(() => []),
      apiFetch('/api/business/prospects').catch(() => []),
      apiFetch('/api/grimoire/summary').catch(() => null),
      apiFetch('/api/grimoire/sessions?limit=10').catch(() => []),
    ]);

    container.innerHTML = `
      <div class="business-view">
        <div class="business-view-header">
          <h2>Business Intelligence</h2>
          <span class="business-year">${year}</span>
        </div>

        <div class="business-top-row">
          ${renderRevenueCard(revenue, year)}
          ${renderCapacityCard(capacity)}
        </div>

        ${renderPipelineSection(pipeline)}
        ${renderIntakeProspectsSection(prospects)}
        ${renderRetentionSection(retention)}
        ${renderGrimoireLeadsSection(grimoireSummary, grimoireSessions)}
        ${renderDavidMaintenanceCard()}
      </div>
    `;
  } catch (err) {
    container.innerHTML = '<div class="error-state">Failed to load business data</div>';
  }
}

function renderRevenueCard(data, year) {
  return `
    <div class="business-card revenue-card">
      <div class="biz-card-title">Revenue ${year}</div>
      <div class="revenue-row">
        <span class="revenue-label">Contracted</span>
        <span class="revenue-value">${formatCurrency(data.total_contracted || 0)}</span>
      </div>
      <div class="revenue-row">
        <span class="revenue-label">Collected</span>
        <span class="revenue-value collected">${formatCurrency(data.total_collected || 0)}</span>
      </div>
      <div class="revenue-row">
        <span class="revenue-label">Outstanding</span>
        <span class="revenue-value outstanding">${formatCurrency(data.outstanding || 0)}</span>
      </div>
      <div class="revenue-sub">${data.show_count || 0} show${data.show_count != 1 ? 's' : ''} this year</div>
    </div>
  `;
}

function renderCapacityCard(data) {
  const avg = Math.round(parseFloat(data.avg_completion) || 0);
  return `
    <div class="business-card capacity-card">
      <div class="biz-card-title">Current Load</div>
      <div class="capacity-stat">
        <span class="capacity-num">${data.active_clients || 0}</span>
        <span class="capacity-label">active clients</span>
      </div>
      <div class="capacity-stat">
        <span class="capacity-num">${data.active_shows || 0}</span>
        <span class="capacity-label">active shows</span>
      </div>
      <div class="capacity-progress-row">
        <span class="capacity-label">Avg completion</span>
        <div class="mini-progress-track">
          <div class="mini-progress-fill" style="width: ${avg}%"></div>
        </div>
        <span class="capacity-pct">${avg}%</span>
      </div>
    </div>
  `;
}

function renderPipelineSection(clients) {
  const stages = {
    prospect:   clients.filter(c => c.client_status === 'prospect'),
    onboarding: clients.filter(c => c.client_status === 'onboarding'),
    active:     clients.filter(c => c.client_status === 'active'),
    complete:   clients.filter(c => c.client_status === 'complete'),
    other:      clients.filter(c => !['prospect','onboarding','active','complete'].includes(c.client_status)),
  };

  const stageEntries = Object.entries(stages).filter(([, list]) => list.length > 0);
  if (stageEntries.length === 0) return '';

  return `
    <div class="pipeline-section">
      <div class="section-label">Pipeline</div>
      <div class="pipeline-stages">
        ${Object.entries(stages).map(([stage, list]) => `
          <div class="pipeline-stage">
            <div class="stage-header">
              <span class="stage-name">${stage}</span>
              <span class="stage-count">${list.length}</span>
            </div>
            <div class="stage-clients">
              ${list.slice(0, 6).map(c => `
                <div class="pipeline-client" onclick="if(window.renderClientCard)window.renderClientCard(${c.id})">${c.name}</div>
              `).join('') || '<div class="biz-empty">—</div>'}
              ${list.length > 6 ? `<div class="biz-more">+${list.length - 6} more</div>` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderRetentionSection(clients) {
  if (!clients || clients.length === 0) return '';

  return `
    <div class="retention-section">
      <div class="section-label">Retention</div>
      <div class="retention-table-wrap">
        <table class="retention-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Program</th>
              <th>First Season</th>
              <th>Last Season</th>
              <th>Seasons</th>
              <th>Shows</th>
            </tr>
          </thead>
          <tbody>
            ${clients.map(c => `
              <tr>
                <td>${c.name}</td>
                <td class="muted">${c.ensemble_type || '—'}</td>
                <td>${c.first_season || '—'}</td>
                <td>${c.last_season || '—'}</td>
                <td class="centered">${c.seasons_count}</td>
                <td class="centered">${c.total_shows}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

// ── Intake Prospects Section ───────────────────────────────────────────────

const LEAD_STATUS_LABELS = {
  new:       'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  dead:      'Dead',
};

const RESEARCH_STATUS_ICONS = {
  pending:  { icon: '○', cls: 'rs-pending',  title: 'Research pending' },
  running:  { icon: '◌', cls: 'rs-running',  title: 'Research in progress' },
  complete: { icon: '✓', cls: 'rs-complete', title: 'Dossier ready' },
  failed:   { icon: '✕', cls: 'rs-failed',   title: 'Research failed' },
};

function relativeDate(iso) {
  if (!iso) return '';
  const ms  = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60)    return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)     return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30)      return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function renderIntakeProspectsSection(prospects) {
  if (!prospects || prospects.length === 0) return '';

  const active = prospects.filter(p => p.lead_status !== 'converted');
  const converted = prospects.filter(p => p.lead_status === 'converted');

  return `
    <div class="intake-prospects-section">
      <div class="section-label" style="display:flex;align-items:center;gap:0.6rem;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:0.6rem;">
          Intake Prospects
          ${active.length ? `<span class="prospect-count-badge">${active.length}</span>` : ''}
        </div>
        <div style="display:flex;gap:6px;align-items:center;">
          <button onclick="window.bizScanGmail()" id="biz-scan-gmail-btn"
            style="background:rgba(0,180,216,.12);color:var(--cyan);border:1px solid rgba(0,180,216,.3);border-radius:6px;padding:4px 10px;font-size:.75rem;font-weight:700;cursor:pointer;">
            ⟳ Scan Gmail
          </button>
          <span class="auto-inline-hint" id="ma-status-scan_gmail_inbox-g" style="font-size:.72rem;"></span>
        </div>
      </div>
      ${active.length === 0 ? '<div class="biz-empty" style="padding:1rem 0;">No open intake prospects.</div>' : `
        <div class="prospect-grid">
          ${active.map(p => renderProspectCard(p)).join('')}
        </div>
      `}
      ${converted.length > 0 ? `
        <div class="prospect-converted-label">
          ${converted.length} converted prospect${converted.length > 1 ? 's' : ''}
          <span class="prospect-converted-names">${converted.map(p => p.school).join(', ')}</span>
        </div>
      ` : ''}
    </div>
  `;
}

function renderProspectCard(p) {
  const rs   = RESEARCH_STATUS_ICONS[p.research_status] || RESEARCH_STATUS_ICONS.pending;
  const meta = [p.circuit, p.classification, p.program_size ? `${p.program_size} mbr` : ''].filter(Boolean).join(' · ');

  return `
    <div class="prospect-card" onclick="window.openProspectModal(${p.id})">
      <div class="prospect-card-top">
        <div class="prospect-school">${p.school}</div>
        <span class="prospect-rs-indicator ${rs.cls}" title="${rs.title}">${rs.icon}</span>
      </div>
      <div class="prospect-director">${p.director_name || p.email}</div>
      ${p.state ? `<div class="prospect-meta">${p.state}${meta ? ' · ' + meta : ''}</div>` : (meta ? `<div class="prospect-meta">${meta}</div>` : '')}
      <div class="prospect-card-footer">
        <span class="prospect-status-badge ps-${p.lead_status}">${LEAD_STATUS_LABELS[p.lead_status] || p.lead_status}</span>
        <span class="prospect-date">${relativeDate(p.created_at)}</span>
      </div>
    </div>
  `;
}

// ── Prospect Detail Modal ──────────────────────────────────────────────────

async function openProspectModal(prospectId) {
  document.getElementById('prospect-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'prospect-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = '<div class="modal-box prospect-modal-box"><div style="padding:2rem;color:#7a9ab8;">Loading…</div></div>';
  document.body.appendChild(modal);
  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });

  try {
    const p = await apiFetch(`/api/prospects/${prospectId}`);
    modal.querySelector('.modal-box').innerHTML = renderProspectModalContent(p);
  } catch (err) {
    modal.querySelector('.modal-box').innerHTML = `<div style="padding:2rem;color:#ff6b6b;">Failed to load prospect: ${err.message}</div>`;
  }
}

function renderProspectModalContent(p) {
  const meta = [p.circuit, p.classification, p.program_size ? `${p.program_size} members` : ''].filter(Boolean).join(' · ');
  const isConverted = p.lead_status === 'converted';

  const statusOptions = ['new','contacted','qualified','converted','dead']
    .map(s => `<option value="${s}" ${p.lead_status === s ? 'selected' : ''}>${LEAD_STATUS_LABELS[s]}</option>`)
    .join('');

  return `
    <div class="modal-header" style="position:sticky;top:0;background:#111d2e;z-index:1;border-radius:10px 10px 0 0;">
      <div>
        <span class="modal-title">${p.school}</span>
        ${p.state ? `<span style="color:#7a9ab8;font-size:0.8rem;margin-left:0.5rem;">${p.state}</span>` : ''}
        ${meta ? `<div style="font-size:0.75rem;color:#5a7a9a;margin-top:0.2rem;">${meta}</div>` : ''}
      </div>
      <button class="modal-close" onclick="document.getElementById('prospect-modal').remove()">✕</button>
    </div>

    <div class="prospect-modal-tabs">
      <button class="prospect-tab-btn active" data-tab="intake" onclick="window.switchProspectTab('intake')">Intake &amp; Notes</button>
      <button class="prospect-tab-btn" data-tab="dossier" onclick="window.switchProspectTab('dossier')">David's Dossier</button>
    </div>

    <div class="modal-body prospect-modal-body">

      <!-- Tab 1: Intake & Notes -->
      <div id="prospect-tab-intake" class="prospect-tab-pane">
        <div class="prospect-field-grid">
          ${prospectField('Director', p.director_name)}
          ${prospectField('Email', p.email ? `<a href="mailto:${p.email}" style="color:#00b4d8;">${p.email}</a>` : null)}
          ${prospectField('State', p.state)}
          ${prospectField('Circuit', p.circuit)}
          ${prospectField('Classification', p.classification)}
          ${prospectField('Program Size', p.program_size)}
          ${prospectField('Source', p.source_form)}
          ${prospectField('Received', p.created_at ? new Date(p.created_at).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : null)}
        </div>

        ${p.strengths ? `<div class="prospect-text-block"><div class="ptb-label">Strengths</div><div class="ptb-body">${escHtml(p.strengths)}</div></div>` : ''}
        ${p.growth_areas ? `<div class="prospect-text-block"><div class="ptb-label">Growth Areas</div><div class="ptb-body">${escHtml(p.growth_areas)}</div></div>` : ''}
        ${p.other_notes ? `<div class="prospect-text-block"><div class="ptb-label">Other Notes</div><div class="ptb-body">${escHtml(p.other_notes)}</div></div>` : ''}

        <div class="prospect-notes-row">
          <label class="cfg-label">Lead Status</label>
          <select class="cfg-select" onchange="window.updateProspectStatus(${p.id}, this.value)" style="max-width:180px;">
            ${statusOptions}
          </select>
        </div>

        <div class="prospect-notes-row" style="margin-top:0.8rem;">
          <label class="cfg-label">Ryan's Notes</label>
          <textarea class="cfg-text prospect-notes-ta"
            placeholder="Your notes on this prospect…"
            oninput="window.saveProspectNotes(${p.id}, this.value)"
          >${escHtml(p.ryan_notes || '')}</textarea>
        </div>
      </div>

      <!-- Tab 2: David's Dossier -->
      <div id="prospect-tab-dossier" class="prospect-tab-pane" style="display:none;">
        ${renderDossierTab(p)}
      </div>

    </div>

    <!-- Action bar -->
    <div class="prospect-modal-actions">
      ${!isConverted ? `
        <button class="btn-primary prospect-convert-btn"
          onclick="window.convertProspectToClient(${p.id}, '${escHtml(p.school)}')">
          Create Client Card →
        </button>
      ` : `
        <span style="color:#22c55e;font-size:0.85rem;">
          ✓ Converted to client #${p.converted_client_id}
        </span>
      `}
    </div>
  `;
}

function renderDossierTab(p) {
  const rs = p.research_status;

  if (rs === 'pending' || rs === 'running') {
    return `
      <div class="dossier-waiting">
        <div class="dossier-spinner">◌</div>
        <div>${rs === 'running' ? 'David is researching this school…' : 'Research queued — check back in a moment.'}</div>
        <button class="btn-outline" style="margin-top:1rem;" onclick="window.openProspectModal(${p.id})">Refresh</button>
      </div>
    `;
  }

  if (rs === 'failed') {
    const err = p.research_data?.error || 'Unknown error';
    return `
      <div class="dossier-waiting">
        <div style="color:#ff6b6b;font-size:1.1rem;">✕ Research failed</div>
        <div style="font-size:0.8rem;color:#7a9ab8;margin-top:0.3rem;">${escHtml(err)}</div>
        <button class="btn-outline" style="margin-top:1rem;"
          onclick="window.refreshProspectDossier(${p.id})">Retry Research</button>
      </div>
    `;
  }

  if (rs !== 'complete' || !p.research_data) {
    return '<div class="dossier-waiting">No dossier data available.</div>';
  }

  const d = p.research_data;
  const nces = d.nces || {};
  const ses  = d.ses  || {};
  const comp = d.competitive || {};
  const sig  = d.signals || {};
  const strat = d.strategy || {};

  return `
    <!-- District Profile -->
    <div class="dossier-section">
      <div class="dossier-section-title">District Profile</div>
      <div class="dossier-metrics">
        ${nces.district_name ? dossierMetric('District', nces.district_name) : ''}
        ${nces.locale ? dossierMetric('Locale', `${nces.locale}${nces.locale_detail ? ' · ' + nces.locale_detail : ''}`) : ''}
        ${nces.enrollment_total != null ? dossierMetric('Enrollment', nces.enrollment_total.toLocaleString()) : ''}
        ${nces.free_reduced_lunch_pct != null ? dossierMetric('Free/Reduced Lunch', `${nces.free_reduced_lunch_pct}%`) : ''}
        ${nces.title_i_status != null ? dossierMetric('Title I', nces.title_i_status ? 'Yes' : 'No') : ''}
        ${nces.per_pupil_expenditure ? dossierMetric('Per-Pupil Spend', `$${Number(nces.per_pupil_expenditure).toLocaleString()}`) : ''}
      </div>
      ${ses.ses_tier ? `<div class="dossier-ses-badge ses-${ses.ses_tier.replace('-','')}">${ses.ses_tier.replace('-',' ')}</div>` : ''}
      ${ses.funding_context ? `<div class="dossier-note">${escHtml(ses.funding_context)}</div>` : ''}
      ${nces.data_year ? `<div class="dossier-footnote">Data year: ${nces.data_year} · Confidence: ${nces.confidence || 'unknown'}</div>` : ''}
    </div>

    <!-- Competitive Context -->
    <div class="dossier-section">
      <div class="dossier-section-title">Competitive Context</div>
      ${comp.existing_wmm_clients_in_circuit?.length ? `
        <div class="dossier-highlight">
          ⚡ ${comp.existing_wmm_clients_in_circuit.length} existing WMM client(s) in this circuit:
          ${comp.existing_wmm_clients_in_circuit.map(c =>
            `<span class="dossier-client-link" onclick="document.getElementById('prospect-modal').remove();if(window.renderClientCard)window.renderClientCard(${c.client_id})">${escHtml(c.name)}</span>`
          ).join(', ')}
        </div>
      ` : ''}
      ${comp.existing_wmm_clients_in_state?.length && !comp.existing_wmm_clients_in_circuit?.length ? `
        <div class="dossier-note">
          ${comp.existing_wmm_clients_in_state.length} WMM client(s) in ${p.state} (different circuits):
          ${comp.existing_wmm_clients_in_state.map(c => escHtml(c.name)).join(', ')}
        </div>
      ` : ''}
      <div class="dossier-note" style="margin-top:0.4rem;">${escHtml(comp.circuit_notes || 'No circuit notes.')}</div>
    </div>

    <!-- Intake Signals -->
    <div class="dossier-section">
      <div class="dossier-section-title">Intake Signals</div>
      <div class="dossier-metrics">
        ${sig.urgency ? dossierMetric('Urgency', sig.urgency) : ''}
        ${sig.experience_level ? dossierMetric('Experience', sig.experience_level.replace(/-/g,' ')) : ''}
        ${sig.ambition_level ? dossierMetric('Ambition', sig.ambition_level) : ''}
      </div>
      ${sig.high_intent_flags?.length ? `
        <div class="dossier-flags">
          ${sig.high_intent_flags.map(f => `<span class="dossier-flag-high">${escHtml(f)}</span>`).join('')}
        </div>
      ` : ''}
      ${sig.red_flags?.length ? `
        <div class="dossier-flags" style="margin-top:0.4rem;">
          ${sig.red_flags.map(f => `<span class="dossier-flag-warn">${escHtml(f)}</span>`).join('')}
        </div>
      ` : ''}
      ${sig.inferred_scope ? `<div class="dossier-note" style="margin-top:0.6rem;"><strong>Inferred scope:</strong> ${escHtml(sig.inferred_scope)}</div>` : ''}
      ${sig.raw_analysis ? `<div class="dossier-note" style="margin-top:0.3rem;">${escHtml(sig.raw_analysis)}</div>` : ''}
    </div>

    <!-- CSM Strategy -->
    <div class="dossier-section">
      <div class="dossier-section-title">David's Strategy</div>
      ${strat.strategic_read ? `<div class="dossier-note" style="margin-bottom:0.8rem;color:#e8f4fd;">${escHtml(strat.strategic_read)}</div>` : ''}
      ${strat.timing_assessment ? dossierBlock('Timing', strat.timing_assessment) : ''}
      ${strat.recommended_approach ? dossierBlock('Recommended Approach', strat.recommended_approach) : ''}
      ${strat.what_to_avoid ? dossierBlock('What to Avoid', strat.what_to_avoid) : ''}
      ${strat.suggested_opener ? `
        <div class="dossier-opener-block">
          <div class="dossier-block-label">Suggested Opener</div>
          <div class="dossier-opener-text">${escHtml(strat.suggested_opener)}</div>
        </div>
      ` : ''}
      ${strat.pricing_tier_note ? dossierBlock('Pricing Context', strat.pricing_tier_note) : ''}
    </div>

    <div style="text-align:center;margin-top:0.5rem;">
      <button class="btn-outline" style="font-size:0.78rem;"
        onclick="window.refreshProspectDossier(${p.id})">Refresh Dossier</button>
    </div>
  `;
}

// ── Dossier helpers ────────────────────────────────────────────────────────

function prospectField(label, value) {
  if (!value) return '';
  return `
    <div class="prospect-field">
      <div class="pf-label">${label}</div>
      <div class="pf-value">${value}</div>
    </div>
  `;
}

function dossierMetric(label, value) {
  return `
    <div class="dossier-metric">
      <div class="dm-label">${label}</div>
      <div class="dm-value">${escHtml(String(value))}</div>
    </div>
  `;
}

function dossierBlock(label, text) {
  return `
    <div class="dossier-block" style="margin-bottom:0.6rem;">
      <div class="dossier-block-label">${label}</div>
      <div class="dossier-note">${escHtml(text)}</div>
    </div>
  `;
}

function escHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g,'&amp;').replace(/</g,'&lt;')
    .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

// ── Tab switching ──────────────────────────────────────────────────────────

function switchProspectTab(tab) {
  document.querySelectorAll('.prospect-tab-btn').forEach(b => b.classList.toggle('active', b.dataset.tab === tab));
  document.querySelectorAll('.prospect-tab-pane').forEach(p => { p.style.display = p.id === `prospect-tab-${tab}` ? 'block' : 'none'; });
}

// ── Prospect status update ─────────────────────────────────────────────────

async function updateProspectStatus(prospectId, status) {
  try {
    await apiFetch(`/api/prospects/${prospectId}`, {
      method: 'PATCH',
      body:   JSON.stringify({ lead_status: status }),
    });
  } catch (err) {
    console.error('[Prospects] Status update failed:', err.message);
  }
}

// ── Notes auto-save ────────────────────────────────────────────────────────

let _prospectNoteTimer = null;
function saveProspectNotes(prospectId, value) {
  clearTimeout(_prospectNoteTimer);
  _prospectNoteTimer = setTimeout(async () => {
    try {
      await apiFetch(`/api/prospects/${prospectId}`, {
        method: 'PATCH',
        body:   JSON.stringify({ ryan_notes: value }),
      });
    } catch (err) {
      console.error('[Prospects] Notes save failed:', err.message);
    }
  }, 800);
}

// ── Refresh dossier ────────────────────────────────────────────────────────

async function refreshProspectDossier(prospectId) {
  try {
    await apiFetch(`/api/prospects/${prospectId}/refresh-research`, { method: 'POST' });
    // Re-open modal to show pending state
    await openProspectModal(prospectId);
  } catch (err) {
    console.error('[Prospects] Refresh failed:', err.message);
  }
}

// ── Convert prospect to client ─────────────────────────────────────────────

async function convertProspectToClient(prospectId, schoolName) {
  if (!confirm(`Convert ${schoolName} to a full client record?\n\nThis will create a client card pre-populated with all intake data.`)) return;

  const btn = document.querySelector('.prospect-convert-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Creating…'; }

  try {
    const result = await apiFetch(`/api/prospects/${prospectId}/convert`, { method: 'POST' });
    document.getElementById('prospect-modal')?.remove();

    // Refresh the business view, then open the new client card
    if (window.renderBusinessView) await renderBusinessView();
    if (window.renderClientCard && result.client?.id) {
      window.renderClientCard(result.client.id);
    }
  } catch (err) {
    alert('Conversion failed: ' + err.message);
    if (btn) { btn.disabled = false; btn.textContent = 'Create Client Card →'; }
  }
}

// ── Grimoire Leads Section ─────────────────────────────────────────────────

function renderGrimoireLeadsSection(summary, sessions) {
  if (!summary && (!sessions || sessions.length === 0)) return '';

  const s = summary || {};
  const directorStarted   = parseInt(s.director_started)   || 0;
  const directorCompleted = parseInt(s.director_completed) || 0;
  const directorEmails    = parseInt(s.director_emails)    || 0;
  const studentDraws      = parseInt(s.student_draws)      || 0;
  const studentEmails     = parseInt(s.student_emails)     || 0;

  const sessionRows = (sessions || []).map(sess => {
    const isDirector = sess.stream === 'director';
    const status = sess.completed_at ? 'completed' : `round ${sess.current_round}`;
    const streamBadge = isDirector
      ? `<span style="background:rgba(0,180,216,.15);color:var(--cyan);padding:1px 6px;border-radius:4px;font-size:.7rem;">Director</span>`
      : `<span style="background:rgba(120,80,200,.15);color:#a78bfa;padding:1px 6px;border-radius:4px;font-size:.7rem;">Student</span>`;
    const clickAttr = isDirector && sess.completed_at
      ? `onclick="window.openOrdealTranscriptModal('${sess.id}')" style="cursor:pointer;" title="View transcript"`
      : '';
    return `
      <tr ${clickAttr}>
        <td>${streamBadge}</td>
        <td style="color:var(--text-main);font-size:.8rem;">${escHtml(sess.category || '—')}</td>
        <td style="color:var(--text-muted);font-size:.78rem;">${status}</td>
        <td style="color:var(--text-muted);font-size:.78rem;">${sess.email ? escHtml(sess.email) : '<span style="opacity:.4">—</span>'}</td>
        <td style="color:var(--text-muted);font-size:.75rem;white-space:nowrap;">${relativeDate(sess.created_at)}</td>
      </tr>
    `;
  }).join('');

  return `
    <div class="intake-prospects-section" style="margin-top:1.5rem;">
      <div class="section-label" style="display:flex;align-items:center;gap:0.6rem;justify-content:space-between;">
        <span>Hard Questions Leads — Last 30 Days</span>
        <button onclick="window.bizDownloadGrimoireCSV()"
          style="background:rgba(0,180,216,.12);color:var(--cyan);border:1px solid rgba(0,180,216,.3);border-radius:6px;padding:4px 10px;font-size:.75rem;font-weight:700;cursor:pointer;">
          ↓ Download CSV
        </button>
      </div>

      <div style="display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:1rem;">
        ${grimoireStat('Ordeals started', directorStarted)}
        ${grimoireStat('Ordeals completed', directorCompleted)}
        ${grimoireStat('Director emails', directorEmails)}
        ${grimoireStat('Student draws', studentDraws)}
        ${grimoireStat('Student emails', studentEmails)}
      </div>

      ${sessions && sessions.length > 0 ? `
        <div style="overflow-x:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:.82rem;">
            <thead>
              <tr style="border-bottom:1px solid rgba(255,255,255,.07);">
                <th style="text-align:left;padding:4px 8px;color:var(--text-muted);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;">Stream</th>
                <th style="text-align:left;padding:4px 8px;color:var(--text-muted);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;">Category</th>
                <th style="text-align:left;padding:4px 8px;color:var(--text-muted);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;">Status</th>
                <th style="text-align:left;padding:4px 8px;color:var(--text-muted);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;">Email</th>
                <th style="text-align:left;padding:4px 8px;color:var(--text-muted);font-weight:600;font-size:.72rem;text-transform:uppercase;letter-spacing:.04em;">When</th>
              </tr>
            </thead>
            <tbody>
              ${sessionRows}
            </tbody>
          </table>
        </div>
      ` : '<div class="biz-empty" style="padding:.8rem 0;">No sessions yet.</div>'}
    </div>
  `;
}

function grimoireStat(label, value) {
  return `
    <div style="background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.07);border-radius:8px;padding:.6rem .9rem;min-width:100px;">
      <div style="font-size:1.4rem;font-weight:700;color:var(--text-main);">${value}</div>
      <div style="font-size:.72rem;color:var(--text-muted);margin-top:2px;">${label}</div>
    </div>
  `;
}

async function openOrdealTranscriptModal(sessionId) {
  document.getElementById('ordeal-transcript-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'ordeal-transcript-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = '<div class="modal-box" style="max-width:680px;"><div style="padding:2rem;color:#7a9ab8;">Loading transcript…</div></div>';
  document.body.appendChild(modal);
  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });

  try {
    const { session, turns } = await apiFetch(`/api/grimoire/sessions/${sessionId}`);

    const turnHtml = (turns || []).map(t => {
      const isMage = t.role === 'mage';
      return `
        <div style="margin-bottom:1rem;padding:.75rem;background:${isMage ? 'rgba(0,180,216,.06)' : 'rgba(255,255,255,.03)'};border-radius:6px;border-left:3px solid ${isMage ? 'var(--cyan)' : 'rgba(255,255,255,.15)'};">
          <div style="font-size:.7rem;font-weight:700;color:${isMage ? 'var(--cyan)' : 'var(--text-muted)'};text-transform:uppercase;letter-spacing:.06em;margin-bottom:.4rem;">
            ${isMage ? 'The Mage' : 'Director'} — Round ${t.round}
          </div>
          <div style="font-size:.85rem;color:var(--text-main);line-height:1.6;white-space:pre-wrap;">${escHtml(t.content)}</div>
        </div>
      `;
    }).join('');

    const dateStr = new Date(session.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    modal.querySelector('.modal-box').innerHTML = `
      <div class="modal-header" style="position:sticky;top:0;background:#111d2e;z-index:1;border-radius:10px 10px 0 0;">
        <div>
          <span class="modal-title">Director Ordeal</span>
          <div style="font-size:.78rem;color:#7a9ab8;margin-top:.2rem;">${escHtml(session.category || 'random')} · ${dateStr}${session.email ? ' · ' + escHtml(session.email) : ''}</div>
        </div>
        <button class="modal-close" onclick="document.getElementById('ordeal-transcript-modal').remove()">✕</button>
      </div>
      <div class="modal-body" style="padding:1.2rem;">
        ${turnHtml || '<div style="color:#7a9ab8;padding:1rem 0;">No turns recorded.</div>'}
      </div>
    `;
  } catch (err) {
    modal.querySelector('.modal-box').innerHTML = `<div style="padding:2rem;color:#ff6b6b;">Failed to load transcript: ${err.message}</div>`;
  }
}

window.bizDownloadGrimoireCSV = function() {
  window.location.href = '/api/grimoire/export.csv';
};

// ── David Maintenance Card ──────────────────────────────────────────────────

function renderDavidMaintenanceCard() {
  return `
    <div style="margin-top:1.5rem;padding:1rem;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:8px;">
      <div style="font-size:.78rem;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:.75rem;">
        David — Maintenance
      </div>
      <div style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;">
        <button onclick="window.bizSendDailyBriefing()" id="biz-daily-briefing-btn"
          style="background:rgba(0,180,216,.1);color:var(--cyan);border:1px solid rgba(0,180,216,.25);border-radius:6px;padding:5px 12px;font-size:.8rem;font-weight:600;cursor:pointer;">
          Send Daily Briefing
        </button>
        <span class="auto-inline-hint" id="ma-status-send_daily_briefing_now-g" style="font-size:.75rem;"></span>

        <button onclick="window.bizSendMemoryDigest()" id="biz-memory-digest-btn"
          style="background:rgba(0,180,216,.1);color:var(--cyan);border:1px solid rgba(0,180,216,.25);border-radius:6px;padding:5px 12px;font-size:.8rem;font-weight:600;cursor:pointer;">
          Send Memory Digest
        </button>
        <span class="auto-inline-hint" id="ma-status-send_memory_digest_now-g" style="font-size:.75rem;"></span>

        <button onclick="window.bizMemoryExpiration()" id="biz-memory-sweep-btn"
          style="background:rgba(255,255,255,.05);color:var(--text-muted);border:1px solid rgba(255,255,255,.1);border-radius:6px;padding:5px 12px;font-size:.8rem;font-weight:600;cursor:pointer;">
          Memory Expiry Sweep
        </button>
        <span class="auto-inline-hint" id="ma-status-run_memory_expiration_sweep-g" style="font-size:.75rem;"></span>

        <button onclick="window.bizResetDavidSession()" id="biz-reset-david-btn"
          style="background:rgba(255,45,120,.1);color:#ff2d78;border:1px solid rgba(255,45,120,.25);border-radius:6px;padding:5px 12px;font-size:.8rem;font-weight:600;cursor:pointer;">
          Reset David Session
        </button>
        <span class="auto-inline-hint" id="ma-status-reset_david_session-g" style="font-size:.75rem;"></span>
      </div>
    </div>
  `;
}

window.bizScanGmail = async function() {
  const btn = document.getElementById('biz-scan-gmail-btn');
  if (btn) { btn.textContent = '⏳ Scanning…'; btn.disabled = true; }
  try {
    await runManualAction('scan_gmail_inbox', {}, 'ma-status-scan_gmail_inbox-g');
  } finally {
    if (btn) { btn.textContent = '⟳ Scan Gmail'; btn.disabled = false; }
  }
};

window.bizSendDailyBriefing = async function() {
  const btn = document.getElementById('biz-daily-briefing-btn');
  if (btn) { btn.textContent = '⏳ Sending…'; btn.disabled = true; }
  try {
    await runManualAction('send_daily_briefing_now', {}, 'ma-status-send_daily_briefing_now-g');
  } finally {
    if (btn) { btn.textContent = 'Send Daily Briefing'; btn.disabled = false; }
  }
};

window.bizSendMemoryDigest = async function() {
  const btn = document.getElementById('biz-memory-digest-btn');
  if (btn) { btn.textContent = '⏳ Sending…'; btn.disabled = true; }
  try {
    await runManualAction('send_memory_digest_now', {}, 'ma-status-send_memory_digest_now-g');
  } finally {
    if (btn) { btn.textContent = 'Send Memory Digest'; btn.disabled = false; }
  }
};

window.bizMemoryExpiration = async function() {
  const btn = document.getElementById('biz-memory-sweep-btn');
  if (btn) { btn.textContent = '⏳ Running…'; btn.disabled = true; }
  try {
    await runManualAction('run_memory_expiration_sweep', {}, 'ma-status-run_memory_expiration_sweep-g');
  } finally {
    if (btn) { btn.textContent = 'Memory Expiry Sweep'; btn.disabled = false; }
  }
};

window.bizResetDavidSession = async function() {
  if (!confirm('Reset David\'s session?\n\nThis clears the managed session ID. David will start fresh on next message.')) return;
  const btn = document.getElementById('biz-reset-david-btn');
  if (btn) { btn.textContent = '⏳ Resetting…'; btn.disabled = true; }
  try {
    await runManualAction('reset_david_session', {}, 'ma-status-reset_david_session-g');
  } finally {
    if (btn) { btn.textContent = 'Reset David Session'; btn.disabled = false; }
  }
};
